/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Collapse,
  DatePicker,
  Form,
  Input,
  Table,
  Typography,
  Row,
  Col,
  Space,
  Flex,
  message,
} from "antd";
import { SearchOutlined, ClearOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import useAccessPageState from "../../../hooks/useAccessPageState";
import ProductionOrderService from "../../../service/ERP/ProductionOrder.service";
import { accessColumns, orderColumns } from "./model";

const pdService = ProductionOrderService();

export default function ProductOrderCandidateTable({
  handleEdit,
  handleView,
  selectable = false,
  selectionType = "checkbox",
  selectedRowKeys = [],
  onSelectionChange,
  persistState = true,
  title = "Production Order",
  headerExtra = null,
}) {
  const [form] = Form.useForm();
  const [accessData, setAccessData] = useState([]);
  const [activeSearch, setActiveSearch] = useState([]);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [loadingRows, setLoadingRows] = useState({});
  const fetchedRef = useRef({});
  const { RangePicker } = DatePicker;
  const {
    tablePagination,
    saveState,
    restoreState,
    clearState,
    restoreDefaultPagination,
    handleTableChange,
  } = useAccessPageState({ form, persistPagination: persistState });

  const handleSearch = (forcedValues = null, paginationOverride = null) => {
    const runSearch = (values) => {
      if (persistState) {
        saveState(values, paginationOverride ?? tablePagination);
      }

      const data = { ...values };
      if (data?.pddate) {
        const [pddate_form, pddate_to] = data.pddate.map((item) =>
          dayjs(item).format("YYYY-MM-DD"),
        );
        Object.assign(data, { pddate_form, pddate_to });
        delete data.pddate;
      }

      pdService
        .search(data, { ignoreLoading: Object.keys(data).length !== 0 })
        .then((res) => {
          setAccessData(res.data.data);
        })
        .catch((err) => {
          console.log(err);
          message.error("โหลดรายการ Production Order ไม่สำเร็จ");
        });
    };

    if (useAccessPageState.canUseForcedSearchValues(forcedValues)) {
      runSearch(forcedValues);
      return;
    }

    form.validateFields().then((values) => {
      runSearch(values);
    });
  };

  const handleClear = () => {
    if (persistState) {
      clearState();
    }
    form.resetFields();
    const nextPagination = restoreDefaultPagination();
    handleSearch({}, nextPagination);
  };

  useEffect(() => {
    if (persistState) {
      const restored = restoreState();
      handleSearch(restored?.searchValues ?? null, restored?.tablePagination);
      return () => {};
    }
    handleSearch({});
    return () => {};
  }, []);

  const handleExpand = (expanded, record) => {
    const key = record.pdcode;
    if (!expanded || fetchedRef.current[key]) return;
    fetchedRef.current[key] = true;
    setLoadingRows((prev) => ({ ...prev, [key]: true }));
    pdService
      .get(key, { ignoreLoading: true })
      .then((res) => {
        const orders = Array.isArray(res?.data?.data?.orders) ? res.data.data.orders : [];
        setExpandedOrders((prev) => ({ ...prev, [key]: orders }));
      })
      .catch(() => {
        fetchedRef.current[key] = false;
        message.error("โหลดรายการไม่สำเร็จ");
      })
      .finally(() => {
        setLoadingRows((prev) => ({ ...prev, [key]: false }));
      });
  };

  const columns = accessColumns({
    handleEdit,
    handleView,
    showAction: !selectable,
  });

  const rowSelection = selectable
    ? {
        type: selectionType,
        selectedRowKeys,
        onChange: (nextSelectedRowKeys, selectedRows) => {
          if (typeof onSelectionChange === "function") {
            onSelectionChange(nextSelectedRowKeys, selectedRows);
          }
        },
      }
    : undefined;

  return (
    <Space direction="vertical" size="middle" style={{ display: "flex", position: "relative" }}>
      <Collapse
        size="small"
        onChange={(keys) => setActiveSearch(keys)}
        bordered={false}
        activeKey={activeSearch}
        items={[
          {
            key: "1",
            label: (
              <>
                <SearchOutlined />
                <span> ค้นหา</span>
              </>
            ),
            children: (
              <>
                <br />
                <Form form={form} layout="vertical" autoComplete="off">
                  <Row gutter={[8, 8]}>
                    <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                      <Form.Item label="PD Code" name="pdcode">
                        <Input placeholder="Enter PD Code" onChange={() => handleSearch()} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                      <Form.Item label="PD Date" name="pddate">
                        <RangePicker
                          placeholder={["From Date", "To Date"]}
                          style={{ width: "100%", height: 40 }}
                          onChange={handleSearch}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                      <Form.Item label="สถานะ" name="doc_status">
                        <Input placeholder="Enter สถานะ" onChange={() => handleSearch()} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[8, 8]}>
                    <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                      <Form.Item label="FG Code" name="fg_code">
                        <Input placeholder="Enter FG Code" onChange={() => handleSearch()} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                      <Form.Item label="Job Order" name="jocode">
                        <Input placeholder="Enter Job Order" onChange={() => handleSearch()} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[8, 8]}>
                    <Col xs={24}>
                      <Flex justify="flex-end" gap={8}>
                        <Button type="primary" size="small" className="bn-action" icon={<SearchOutlined />} onClick={() => handleSearch()}>
                          ค้นหา
                        </Button>
                        <Button type="primary" size="small" className="bn-action" danger icon={<ClearOutlined />} onClick={() => handleClear()}>
                          ล้าง
                        </Button>
                      </Flex>
                    </Col>
                  </Row>
                </Form>
              </>
            ),
            showArrow: false,
          },
        ]}
      />
      <Card>
        <Row gutter={[8, 8]} className="m-0">
          <Col xs={24}>
            <Table
              title={() => (
                <Flex className="width-100" align="center">
                  <Col span={12} className="p-0">
                    <Flex gap={4} justify="start" align="center">
                      <Typography.Title className="m-0 !text-zinc-800" level={3}>
                        {title}
                      </Typography.Title>
                    </Flex>
                  </Col>
                  <Col span={12} style={{ paddingInline: 0 }}>
                    <Flex gap={4} justify="end">{headerExtra}</Flex>
                  </Col>
                </Flex>
              )}
              size="small"
              rowKey="pdcode"
              columns={columns}
              dataSource={accessData}
              rowSelection={rowSelection}
              scroll={{ x: "max-content" }}
              pagination={tablePagination}
              onChange={handleTableChange}
              expandable={{
                onExpand: handleExpand,
                expandedRowRender: (record) => (
                  <Table
                    size="small"
                    rowKey="code"
                    columns={orderColumns}
                    dataSource={expandedOrders[record.pdcode] || []}
                    loading={loadingRows[record.pdcode] || false}
                    pagination={false}
                    scroll={{ x: "max-content" }}
                    style={{ margin: "8px 0" }}
                  />
                ),
              }}
            />
          </Col>
        </Row>
      </Card>
    </Space>
  );
}